import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, ArrowLeft, Loader2, Save, Trash2, Plus, X, Upload, KeyRound, CalendarDays, ClipboardList, ChevronDown, ChevronUp } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURRENCY_OPTIONS = [
  { value: "eur", label: "EUR (€)" },
  { value: "usd", label: "USD ($)" },
  { value: "gbp", label: "GBP (£)" },
];

const BLANK_LISTING = { name: "", description: "", rate_per_night: 40, currency: "eur", active: true, image_url: "" };

const AdminDashboard = () => {
  const [listings, setListings] = useState([]);
  const [editStates, setEditStates] = useState({});
  const [saving, setSaving] = useState({});
  const [deleting, setDeleting] = useState({});
  const [uploading, setUploading] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [blockedDatesMap, setBlockedDatesMap] = useState({});
  const [blockedOpen, setBlockedOpen] = useState({});
  const [savingBlocked, setSavingBlocked] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [newListing, setNewListing] = useState({ ...BLANK_LISTING });
  const [addSaving, setAddSaving] = useState(false);
  const [addUploading, setAddUploading] = useState(false);
  const [credForm, setCredForm] = useState({ current_password: "", new_username: "", new_password: "" });
  const [credSaving, setCredSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("properties");
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null); // booking id being edited
  const [bookingEdits, setBookingEdits] = useState({}); // field values for edit form
  const [bookingSaving, setBookingSaving] = useState(false);
  const fileRefs = useRef({});
  const addFileRef = useRef(null);
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem("adminToken");

  const fetchListings = useCallback(async () => {
    const token = getToken();
    if (!token) { navigate("/admin/login"); return; }
    try {
      const res = await axios.get(`${API}/admin/listings`, { headers: { Authorization: `Bearer ${token}` } });
      setListings(res.data);
      const states = {};
      const bDates = {};
      res.data.forEach((l) => {
        states[l.id] = { name: l.name, description: l.description, rate_per_night: l.rate_per_night, weekend_rate: l.weekend_rate || "", currency: l.currency, active: l.active, image_url: l.image_url || "" };
        bDates[l.id] = (l.blocked_dates || []).map(d => { const [y,m,day] = d.split('-').map(Number); return new Date(y, m-1, day); });
      });
      setEditStates(states);
      setBlockedDatesMap(bDates);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem("adminToken"); navigate("/admin/login"); }
      else toast.error("Failed to load listings");
    } finally { setPageLoading(false); }
  }, [navigate]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const fetchBookings = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setBookingsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/bookings`, { headers: { Authorization: `Bearer ${token}` } });
      setBookings(res.data);
    } catch { toast.error("Failed to load bookings"); }
    finally { setBookingsLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === "bookings") fetchBookings();
  }, [activeTab, fetchBookings]);

  const CHECK_IN_TIMES_ADMIN = [];
  for (let h = 14; h <= 22; h++) {
    CHECK_IN_TIMES_ADMIN.push(`${String(h).padStart(2, "0")}:00`);
    if (h < 22) CHECK_IN_TIMES_ADMIN.push(`${String(h).padStart(2, "0")}:30`);
  }
  const CHECK_OUT_TIMES_ADMIN = [];
  for (let h = 0; h <= 11; h++) {
    CHECK_OUT_TIMES_ADMIN.push(`${String(h).padStart(2, "0")}:00`);
    if (h < 11) CHECK_OUT_TIMES_ADMIN.push(`${String(h).padStart(2, "0")}:30`);
  }

  const startEditBooking = (b) => {
    setEditingBooking(b.id);
    setBookingEdits({
      guest_name: b.guest_name || "",
      guest_email: b.guest_email || "",
      guest_phone: b.guest_phone || "",
      guest_count: b.guest_count ?? 1,
      check_in_time: b.check_in_time || "",
      check_out_time: b.check_out_time || "",
      parking_required: b.parking_required || false,
      special_requirements: b.special_requirements || "",
    });
  };

  const handleSaveBooking = async (bookingId) => {
    const token = getToken();
    if (!token) return;
    setBookingSaving(true);
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}`, bookingEdits, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings((prev) => prev.map((b) => b.id === bookingId ? { ...b, ...bookingEdits } : b));
      setEditingBooking(null);
      toast.success("Booking updated");
    } catch { toast.error("Failed to save changes"); }
    finally { setBookingSaving(false); }
  };

  const handleChange = (id, field, value) =>
    setEditStates((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const handleSave = async (listingId) => {
    const token = getToken();
    setSaving((p) => ({ ...p, [listingId]: true }));
    try {
      await axios.put(`${API}/admin/listings/${listingId}`, editStates[listingId], { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Changes saved");
    } catch { toast.error("Failed to save. Please try again."); }
    finally { setSaving((p) => ({ ...p, [listingId]: false })); }
  };

  const handleDelete = async (listingId) => {
    const token = getToken();
    setDeleting((p) => ({ ...p, [listingId]: true }));
    try {
      await axios.delete(`${API}/admin/listings/${listingId}`, { headers: { Authorization: `Bearer ${token}` } });
      setListings((p) => p.filter((l) => l.id !== listingId));
      setConfirmDelete(null);
      toast.success("Property removed");
    } catch { toast.error("Failed to delete."); }
    finally { setDeleting((p) => ({ ...p, [listingId]: false })); }
  };

  const uploadImageFile = async (file, onSuccess) => {
    const token = getToken();
    const formData = new FormData();
    formData.append("file", file);
    const res = await axios.post(`${API}/admin/upload-image`, formData, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
    });
    onSuccess(`${BACKEND_URL}${res.data.url}`);
    toast.success("Photo uploaded");
  };

  const handleUploadImage = async (listingId, file) => {
    setUploading((p) => ({ ...p, [listingId]: true }));
    try {
      await uploadImageFile(file, (url) => handleChange(listingId, "image_url", url));
    } catch { toast.error("Upload failed. Please try again."); }
    finally { setUploading((p) => ({ ...p, [listingId]: false })); }
  };

  const handleAddUploadImage = async (file) => {
    setAddUploading(true);
    try {
      await uploadImageFile(file, (url) => setNewListing((p) => ({ ...p, image_url: url })));
    } catch { toast.error("Upload failed."); }
    finally { setAddUploading(false); }
  };

  const handleSaveBlockedDates = async (listingId) => {
    const token = getToken();
    setSavingBlocked((p) => ({ ...p, [listingId]: true }));
    try {
      const dates = (blockedDatesMap[listingId] || []).map(d => format(d, "yyyy-MM-dd"));
      await axios.put(`${API}/admin/listings/${listingId}/blocked-dates`, { dates }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${dates.length} date${dates.length !== 1 ? "s" : ""} blocked`);
    } catch { toast.error("Failed to save blocked dates."); }
    finally { setSavingBlocked((p) => ({ ...p, [listingId]: false })); }
  };

  const handleCreateListing = async () => {
    if (!newListing.name.trim()) { toast.error("Property name is required"); return; }
    const token = getToken();
    setAddSaving(true);
    try {
      const res = await axios.post(`${API}/admin/listings`, newListing, { headers: { Authorization: `Bearer ${token}` } });
      setListings((p) => [...p, res.data]);
      setEditStates((p) => ({ ...p, [res.data.id]: { name: res.data.name, description: res.data.description, rate_per_night: res.data.rate_per_night, weekend_rate: res.data.weekend_rate || "", currency: res.data.currency, active: res.data.active, image_url: res.data.image_url || "" } }));
      setBlockedDatesMap((p) => ({ ...p, [res.data.id]: [] }));
      setAddOpen(false);
      setNewListing({ ...BLANK_LISTING });
      toast.success(`"${res.data.name}" added`);
    } catch { toast.error("Failed to create listing."); }
    finally { setAddSaving(false); }
  };

  const handleCredChange = async (e) => {
    e.preventDefault();
    if (!credForm.new_username.trim() || !credForm.new_password.trim()) { toast.error("All fields are required"); return; }
    const token = getToken();
    setCredSaving(true);
    try {
      await axios.put(`${API}/admin/credentials`, credForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Credentials updated. Please sign in again.");
      setTimeout(() => { localStorage.removeItem("adminToken"); navigate("/admin/login"); }, 1800);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update credentials."); }
    finally { setCredSaving(false); }
  };

  const handleLogout = () => { localStorage.removeItem("adminToken"); navigate("/admin/login"); };

  if (pageLoading) return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
      <Loader2 className="animate-spin text-[#D4AF37]" size={28} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF9]">

      {/* Add Property Modal */}
      {addOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setAddOpen(false)}
        >
          <div className="bg-white rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between px-7 py-5 border-b border-[#E7E5E4]">
              <h2 className="font-playfair text-xl text-[#1C1917]">Add New Property</h2>
              <button onClick={() => setAddOpen(false)} className="text-[#A8A29E] hover:text-[#1C1917] transition-colors"><X size={18} /></button>
            </div>
            <div className="px-7 py-6 space-y-5">
              {/* Photo upload */}
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#A8A29E] mb-3 font-manrope">Property Photo</label>
                <div
                  onClick={() => addFileRef.current?.click()}
                  className="border-2 border-dashed border-[#E7E5E4] rounded-sm p-5 flex flex-col items-center justify-center cursor-pointer hover:border-[#D4AF37] transition-colors duration-200 min-h-[100px]"
                >
                  {newListing.image_url ? (
                    <img src={newListing.image_url} alt="preview" className="w-full h-32 object-cover rounded-sm" />
                  ) : addUploading ? (
                    <Loader2 className="animate-spin text-[#D4AF37]" size={22} />
                  ) : (
                    <>
                      <Upload size={20} className="text-[#A8A29E] mb-2" />
                      <p className="text-xs text-[#A8A29E] font-manrope">Click to upload photo</p>
                    </>
                  )}
                </div>
                <input ref={addFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && handleAddUploadImage(e.target.files[0])} />
              </div>
              {/* Name */}
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#A8A29E] mb-2 font-manrope">Property Name *</label>
                <input
                  data-testid="new-listing-name-input"
                  type="text" value={newListing.name}
                  onChange={(e) => setNewListing((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. 3BHK Riverside Suite"
                  className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-base text-[#1C1917] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 font-manrope"
                />
              </div>
              {/* Description */}
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#A8A29E] mb-2 font-manrope">Description</label>
                <textarea
                  value={newListing.description}
                  onChange={(e) => setNewListing((p) => ({ ...p, description: e.target.value }))}
                  rows={3}
                  className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-sm text-[#1C1917] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 resize-none font-manrope"
                />
              </div>
              {/* Price + Currency */}
              <div className="flex gap-6">
                <div className="flex-1">
                  <label className="block text-[10px] tracking-widests uppercase text-[#A8A29E] mb-2 font-manrope">Price / Night</label>
                  <input type="number" min="0" step="0.01" value={newListing.rate_per_night}
                    onChange={(e) => setNewListing((p) => ({ ...p, rate_per_night: parseFloat(e.target.value) || 0 }))}
                    className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-base text-[#1C1917] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 font-manrope"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-[10px] tracking-widests uppercase text-[#A8A29E] mb-2 font-manrope">Currency</label>
                  <select value={newListing.currency} onChange={(e) => setNewListing((p) => ({ ...p, currency: e.target.value }))}
                    className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-base text-[#1C1917] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 font-manrope cursor-pointer"
                  >
                    {CURRENCY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              {/* Active */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={newListing.active} onChange={(e) => setNewListing((p) => ({ ...p, active: e.target.checked }))} className="w-4 h-4 accent-[#D4AF37]" />
                <span className="text-sm text-[#1C1917] font-manrope">Make active immediately</span>
              </label>
            </div>
            <div className="px-7 py-5 border-t border-[#E7E5E4] flex justify-end gap-3">
              <button onClick={() => setAddOpen(false)} className="h-11 px-6 border border-[#E7E5E4] text-[#A8A29E] text-[10px] tracking-widest uppercase font-manrope hover:border-[#1C1917] hover:text-[#1C1917] transition-colors rounded-sm">
                Cancel
              </button>
              <button
                data-testid="confirm-add-listing-btn"
                onClick={handleCreateListing} disabled={addSaving}
                className="flex items-center gap-2 h-11 px-7 bg-[#1C1917] text-[#D4AF37] text-[10px] tracking-widest uppercase font-semibold font-manrope hover:bg-[#292524] transition-colors rounded-sm disabled:opacity-60"
              >
                {addSaving ? <><Loader2 className="animate-spin" size={13} />Adding…</> : <><Plus size={13} />Add Property</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-[#E7E5E4] px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <button data-testid="back-to-portal-btn" onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[10px] text-[#A8A29E] hover:text-[#1C1917] transition-colors tracking-widest uppercase font-manrope">
            <ArrowLeft size={13} />Portal
          </button>
          <div className="w-px h-4 bg-[#E7E5E4]" />
          <span className="font-playfair text-sm text-[#1C1917]">Goldline Management</span>
          <span className="text-[10px] bg-[#F5F5F4] text-[#A8A29E] px-2 py-0.5 rounded-sm tracking-widest uppercase font-manrope">Admin</span>
        </div>
        <button data-testid="admin-logout-btn" onClick={handleLogout}
          className="flex items-center gap-2 text-[10px] text-[#A8A29E] hover:text-[#1C1917] transition-colors tracking-widest uppercase font-manrope">
          <LogOut size={13} />Sign Out
        </button>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-[#E7E5E4] px-6">
        <div className="flex gap-0 max-w-3xl mx-auto">
          <button
            data-testid="tab-properties"
            onClick={() => setActiveTab("properties")}
            className={`flex items-center gap-2 px-4 py-3 text-[10px] tracking-widest uppercase font-manrope border-b-2 transition-colors ${
              activeTab === "properties"
                ? "border-[#D4AF37] text-[#1C1917]"
                : "border-transparent text-[#A8A29E] hover:text-[#1C1917]"
            }`}
          >
            <Plus size={12} />Properties
          </button>
          <button
            data-testid="tab-bookings"
            onClick={() => setActiveTab("bookings")}
            className={`flex items-center gap-2 px-4 py-3 text-[10px] tracking-widest uppercase font-manrope border-b-2 transition-colors ${
              activeTab === "bookings"
                ? "border-[#D4AF37] text-[#1C1917]"
                : "border-transparent text-[#A8A29E] hover:text-[#1C1917]"
            }`}
          >
            <ClipboardList size={12} />Booking Logs
            {bookings.filter(b => b.payment_status === "paid").length > 0 && (
              <span className="bg-[#D4AF37] text-[#1C1917] text-[9px] font-bold px-1.5 py-0.5 rounded-sm">
                {bookings.filter(b => b.payment_status === "paid").length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-10">

        {activeTab === "properties" && (<>
        {/* Section Header */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#A8A29E] mb-2 font-manrope">Manage Listings</p>
            <h1 className="text-4xl font-playfair text-[#1C1917] tracking-tight">Properties</h1>
          </div>
          <button
            data-testid="add-property-btn"
            onClick={() => setAddOpen(true)}
            className="flex items-center gap-2 h-11 px-6 bg-[#1C1917] text-[#D4AF37] text-[10px] tracking-widest uppercase font-semibold font-manrope hover:bg-[#292524] transition-colors rounded-sm"
          >
            <Plus size={13} />Add Property
          </button>
        </div>
        <div className="space-y-6">
          {listings.map((listing, idx) => {
            const edit = editStates[listing.id] || {};
            if (!fileRefs.current[listing.id]) fileRefs.current[listing.id] = React.createRef();
            return (
              <div key={listing.id} data-testid={`listing-admin-card-${listing.id}`} className="bg-white border border-[#E7E5E4] rounded-sm p-6">

                {/* Card Header */}
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] tracking-widest text-[#A8A29E] uppercase font-manrope">Property {String(idx + 1).padStart(2, "0")}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-sm tracking-widest uppercase font-manrope ${edit.active ? "bg-emerald-50 text-emerald-600" : "bg-[#F5F5F4] text-[#A8A29E]"}`}>
                      {edit.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-xs text-[#A8A29E] font-manrope">Show</span>
                      <input data-testid={`listing-active-toggle-${listing.id}`} type="checkbox"
                        checked={edit.active || false}
                        onChange={(e) => handleChange(listing.id, "active", e.target.checked)}
                        className="w-4 h-4 cursor-pointer accent-[#D4AF37]" />
                    </label>
                    {/* Delete with inline confirm */}
                    {confirmDelete === listing.id ? (
                      <div className="flex items-center gap-2 bg-red-50 px-3 py-1.5 rounded-sm">
                        <span className="text-xs text-red-500 font-manrope">Remove this property?</span>
                        <button onClick={() => handleDelete(listing.id)} disabled={deleting[listing.id]}
                          className="text-[10px] px-2.5 py-1 bg-red-500 text-white rounded-sm font-manrope tracking-wide hover:bg-red-600 transition-colors disabled:opacity-60">
                          {deleting[listing.id] ? <Loader2 className="animate-spin" size={12} /> : "Remove"}
                        </button>
                        <button onClick={() => setConfirmDelete(null)} className="text-[10px] px-2.5 py-1 bg-white border border-red-200 text-red-400 rounded-sm font-manrope hover:bg-red-50 transition-colors">Cancel</button>
                      </div>
                    ) : (
                      <button data-testid={`delete-listing-btn-${listing.id}`} onClick={() => setConfirmDelete(listing.id)}
                        className="p-1.5 text-[#C8C4C0] hover:text-red-400 transition-colors rounded-sm" title="Remove property">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Photo Upload Row */}
                <div className="mb-5 pb-5 border-b border-[#F5F5F4]">
                  <label className="block text-[10px] tracking-widests uppercase text-[#A8A29E] mb-3 font-manrope">Property Photo</label>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-20 rounded-sm overflow-hidden border border-[#E7E5E4] flex-shrink-0 bg-[#F5F5F4]">
                      {edit.image_url ? (
                        <img src={edit.image_url} alt={edit.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#C8C4C0]"><Upload size={16} /></div>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <button
                        data-testid={`upload-image-btn-${listing.id}`}
                        onClick={() => fileRefs.current[listing.id]?.current?.click()}
                        disabled={uploading[listing.id]}
                        className="flex items-center gap-2 h-9 px-4 border border-[#E7E5E4] text-[#1C1917] text-[10px] tracking-widest uppercase font-manrope hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors rounded-sm disabled:opacity-50"
                      >
                        {uploading[listing.id] ? <><Loader2 className="animate-spin" size={12} />Uploading…</> : <><Upload size={12} />{edit.image_url ? "Change Photo" : "Upload Photo"}</>}
                      </button>
                      {edit.image_url && (
                        <button onClick={() => handleChange(listing.id, "image_url", "")}
                          className="block text-[10px] text-[#A8A29E] hover:text-red-400 transition-colors font-manrope">
                          Remove photo
                        </button>
                      )}
                    </div>
                    <input ref={fileRefs.current[listing.id]} type="file" accept="image/*" className="hidden"
                      onChange={(e) => e.target.files[0] && handleUploadImage(listing.id, e.target.files[0])} />
                  </div>
                </div>

                {/* Editable Fields */}
                <div className="space-y-5">
                  <div>
                    <label className="block text-[10px] tracking-widests uppercase text-[#A8A29E] mb-2 font-manrope">Property Name</label>
                    <input data-testid={`listing-name-input-${listing.id}`} type="text" value={edit.name || ""}
                      onChange={(e) => handleChange(listing.id, "name", e.target.value)}
                      className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-base text-[#1C1917] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 font-manrope" />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-widests uppercase text-[#A8A29E] mb-2 font-manrope">Description</label>
                    <textarea data-testid={`listing-desc-input-${listing.id}`} value={edit.description || ""}
                      onChange={(e) => handleChange(listing.id, "description", e.target.value)}
                      rows={3}
                      className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-sm text-[#1C1917] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 resize-none leading-relaxed font-manrope" />
                  </div>
                  <div className="flex gap-6">
                    <div className="flex-1">
                      <label className="block text-[10px] tracking-widests uppercase text-[#A8A29E] mb-2 font-manrope">Price / Night (Weekday)</label>
                      <input data-testid={`listing-price-input-${listing.id}`} type="number" min="0" step="0.01" value={edit.rate_per_night ?? ""}
                        onChange={(e) => handleChange(listing.id, "rate_per_night", parseFloat(e.target.value) || 0)}
                        className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-base text-[#1C1917] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 font-manrope" />
                    </div>
                    <div className="flex-1">
                      <label className="block text-[10px] tracking-widests uppercase text-[#A8A29E] mb-2 font-manrope">Weekend Price (Fri–Sat)</label>
                      <input data-testid={`listing-weekend-price-input-${listing.id}`} type="number" min="0" step="0.01" value={edit.weekend_rate ?? ""}
                        placeholder="Same as weekday"
                        onChange={(e) => handleChange(listing.id, "weekend_rate", e.target.value === "" ? null : parseFloat(e.target.value) || 0)}
                        className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-base text-[#1C1917] placeholder-[#C8C4C0] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 font-manrope" />
                    </div>
                  </div>
                  <div className="flex gap-6">
                    <div className="flex-1">
                      <label className="block text-[10px] tracking-widests uppercase text-[#A8A29E] mb-2 font-manrope">Currency</label>
                      <select data-testid={`listing-currency-select-${listing.id}`} value={edit.currency || "eur"}
                        onChange={(e) => handleChange(listing.id, "currency", e.target.value)}
                        className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-base text-[#1C1917] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 font-manrope cursor-pointer">
                        {CURRENCY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Save */}
                <div className="mt-7 flex justify-end">
                  <button data-testid={`save-listing-btn-${listing.id}`} onClick={() => handleSave(listing.id)} disabled={saving[listing.id]}
                    className="flex items-center gap-2 h-11 px-7 bg-[#1C1917] text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase font-semibold font-manrope hover:bg-[#292524] transition-colors duration-300 rounded-sm active:scale-[0.99] disabled:opacity-60">
                    {saving[listing.id] ? <><Loader2 className="animate-spin" size={13} />Saving…</> : <><Save size={13} />Save Changes</>}
                  </button>
                </div>

                {/* Blocked Dates */}
                <div className="mt-5 pt-5 border-t border-[#F5F5F4]">
                  <button
                    onClick={() => setBlockedOpen((p) => ({ ...p, [listing.id]: !p[listing.id] }))}
                    className="flex items-center justify-between w-full group"
                  >
                    <div className="flex items-center gap-2">
                      <CalendarDays size={14} className="text-[#A8A29E]" />
                      <span className="text-[10px] tracking-widest uppercase text-[#A8A29E] font-manrope group-hover:text-[#1C1917] transition-colors">
                        Block Dates
                      </span>
                      {(blockedDatesMap[listing.id] || []).length > 0 && (
                        <span className="text-[10px] bg-[#FEF3C7] text-[#C5A059] px-2 py-0.5 rounded-sm font-manrope">
                          {(blockedDatesMap[listing.id] || []).length} blocked
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-[#A8A29E] font-manrope">{blockedOpen[listing.id] ? "▲ Hide" : "▼ Show"}</span>
                  </button>

                  {blockedOpen[listing.id] && (
                    <div className="mt-4">
                      <p className="text-xs text-[#A8A29E] font-manrope mb-3">Click dates to block them — blocked dates will be unavailable for customers to select.</p>
                      <div className="border border-[#E7E5E4] rounded-sm bg-[#FAFAF9] p-2 flex justify-center">
                        <Calendar
                          mode="multiple"
                          selected={blockedDatesMap[listing.id] || []}
                          onSelect={(dates) => setBlockedDatesMap((p) => ({ ...p, [listing.id]: dates || [] }))}
                          disabled={{ before: new Date() }}
                          numberOfMonths={1}
                          classNames={{
                            months: "flex flex-col items-center w-full",
                            month: "w-full",
                            caption: "flex justify-center pt-2 pb-3 relative items-center px-10",
                            caption_label: "font-playfair text-sm font-semibold text-[#1C1917]",
                            nav_button: "h-7 w-7 bg-transparent p-0 text-[#A8A29E] hover:text-[#D4AF37] transition-colors inline-flex items-center justify-center rounded-sm",
                            nav_button_previous: "absolute left-2",
                            nav_button_next: "absolute right-2",
                            table: "w-full border-collapse",
                            head_row: "flex w-full",
                            head_cell: "flex-1 text-center text-[10px] tracking-widest uppercase text-[#A8A29E] font-manrope pb-2 font-normal",
                            row: "flex w-full mb-1",
                            cell: "flex-1 relative p-0 text-center focus-within:relative focus-within:z-20",
                            day: "h-9 w-full p-0 font-normal aria-selected:opacity-100 font-manrope text-sm text-[#1C1917] hover:bg-[#FEF3C7] rounded-sm transition-colors inline-flex items-center justify-center",
                            day_selected: "bg-[#1C1917] text-[#D4AF37] hover:bg-[#292524] hover:text-[#D4AF37] font-semibold rounded-sm",
                            day_today: "text-[#D4AF37] font-bold",
                            day_outside: "text-[#C8C4C0] opacity-40",
                            day_disabled: "text-[#C8C4C0] opacity-30 cursor-not-allowed",
                          }}
                        />
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        {(blockedDatesMap[listing.id] || []).length > 0 && (
                          <button onClick={() => setBlockedDatesMap((p) => ({ ...p, [listing.id]: [] }))}
                            className="text-[10px] text-[#A8A29E] hover:text-red-400 transition-colors font-manrope">
                            Clear all blocked dates
                          </button>
                        )}
                        <div className="ml-auto">
                          <button
                            data-testid={`save-blocked-dates-btn-${listing.id}`}
                            onClick={() => handleSaveBlockedDates(listing.id)}
                            disabled={savingBlocked[listing.id]}
                            className="flex items-center gap-2 h-9 px-5 bg-[#1C1917] text-[#D4AF37] text-[10px] tracking-widest uppercase font-semibold font-manrope hover:bg-[#292524] transition-colors rounded-sm disabled:opacity-60"
                          >
                            {savingBlocked[listing.id] ? <><Loader2 className="animate-spin" size={12} />Saving…</> : <><Save size={12} />Save Blocked Dates</>}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {listings.length === 0 && (
            <div className="text-center py-16 border border-dashed border-[#E7E5E4] rounded-sm">
              <p className="text-sm text-[#A8A29E] font-manrope mb-4">No properties yet</p>
              <button onClick={() => setAddOpen(true)} className="flex items-center gap-2 mx-auto h-11 px-6 bg-[#1C1917] text-[#D4AF37] text-[10px] tracking-widest uppercase font-semibold font-manrope hover:bg-[#292524] transition-colors rounded-sm">
                <Plus size={13} />Add First Property
              </button>
            </div>
          )}
        </div>

        {/* Account & Security */}
        <div className="mt-10 bg-white border border-[#E7E5E4] rounded-sm p-6">
          <div className="flex items-center gap-3 mb-1">
            <KeyRound size={14} className="text-[#A8A29E]" />
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#A8A29E] font-manrope">Account & Security</p>
          </div>
          <h2 className="font-playfair text-2xl text-[#1C1917] mb-6 tracking-tight">Change Credentials</h2>
          <form onSubmit={handleCredChange} className="space-y-5">
            <div>
              <label className="block text-[10px] tracking-widests uppercase text-[#A8A29E] mb-2 font-manrope">Current Password</label>
              <input data-testid="cred-current-password" type="password" required
                value={credForm.current_password}
                onChange={(e) => setCredForm((p) => ({ ...p, current_password: e.target.value }))}
                placeholder="Enter current password"
                className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-base text-[#1C1917] placeholder-[#C8C4C0] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 font-manrope" />
            </div>
            <div className="flex gap-6">
              <div className="flex-1">
                <label className="block text-[10px] tracking-widests uppercase text-[#A8A29E] mb-2 font-manrope">New Username</label>
                <input data-testid="cred-new-username" type="text" required
                  value={credForm.new_username}
                  onChange={(e) => setCredForm((p) => ({ ...p, new_username: e.target.value }))}
                  placeholder="New username"
                  className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-base text-[#1C1917] placeholder-[#C8C4C0] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 font-manrope" />
              </div>
              <div className="flex-1">
                <label className="block text-[10px] tracking-widests uppercase text-[#A8A29E] mb-2 font-manrope">New Password</label>
                <input data-testid="cred-new-password" type="password" required
                  value={credForm.new_password}
                  onChange={(e) => setCredForm((p) => ({ ...p, new_password: e.target.value }))}
                  placeholder="New password"
                  className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-base text-[#1C1917] placeholder-[#C8C4C0] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 font-manrope" />
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <button data-testid="save-credentials-btn" type="submit" disabled={credSaving}
                className="flex items-center gap-2 h-11 px-7 bg-[#1C1917] text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase font-semibold font-manrope hover:bg-[#292524] transition-colors rounded-sm disabled:opacity-60">
                {credSaving ? <><Loader2 className="animate-spin" size={13} />Saving…</> : <><KeyRound size={13} />Update Credentials</>}
              </button>
            </div>
          </form>
        </div>
        </>)}

        {/* Booking Logs Tab */}
        {activeTab === "bookings" && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#A8A29E] mb-2 font-manrope">Payment Records</p>
                <h1 className="text-4xl font-playfair text-[#1C1917] tracking-tight">Booking Logs</h1>
              </div>
              <button
                onClick={fetchBookings}
                disabled={bookingsLoading}
                className="flex items-center gap-2 h-9 px-4 border border-[#E7E5E4] text-[#A8A29E] text-[10px] tracking-widest uppercase font-manrope hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors rounded-sm"
              >
                {bookingsLoading ? <Loader2 className="animate-spin" size={12} /> : "Refresh"}
              </button>
            </div>

            {bookingsLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="animate-spin text-[#D4AF37]" size={24} />
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-20 border border-dashed border-[#E7E5E4] rounded-sm">
                <ClipboardList size={28} className="text-[#C8C4C0] mx-auto mb-3" />
                <p className="text-sm text-[#A8A29E] font-manrope">No bookings yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map((b) => {
                  const isPaid = b.payment_status === "paid";
                  const isExpanded = expandedBooking === b.id;
                  const cur = b.currency?.toUpperCase() || "EUR";
                  const sym = { EUR: "€", USD: "$", GBP: "£" }[cur] || cur;
                  return (
                    <div
                      key={b.id}
                      data-testid={`booking-card-${b.id}`}
                      className={`bg-white border rounded-sm overflow-hidden ${isPaid ? "border-[#E7E5E4]" : "border-[#F5F5F4]"}`}
                    >
                      {/* Booking Header Row */}
                      <div
                        className="flex items-center justify-between p-5 cursor-pointer"
                        onClick={() => setExpandedBooking(isExpanded ? null : b.id)}
                      >
                        <div className="flex items-center gap-4 min-w-0">
                          <span className={`flex-shrink-0 text-[9px] font-bold px-2 py-1 rounded-sm tracking-widest uppercase font-manrope ${isPaid ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-500"}`}>
                            {isPaid ? "Paid" : b.payment_status}
                          </span>
                          <div className="min-w-0">
                            <p className="font-manrope font-semibold text-[#1C1917] text-sm truncate">
                              {b.guest_name || "Unknown Guest"}
                            </p>
                            <p className="text-xs text-[#A8A29E] font-manrope">
                              {b.listing_name} · {b.nights} night{b.nights !== 1 ? "s" : ""}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <div className="text-right">
                            <p className="font-playfair text-lg font-semibold text-[#1C1917]">
                              {sym}{b.amount?.toFixed(2)}
                            </p>
                            <p className="text-[10px] text-[#A8A29E] font-manrope">
                              {b.created_at ? new Date(b.created_at).toLocaleDateString("en-IE", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                            </p>
                          </div>
                          {isExpanded ? <ChevronUp size={14} className="text-[#A8A29E]" /> : <ChevronDown size={14} className="text-[#A8A29E]" />}
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="border-t border-[#F5F5F4] px-5 py-5 bg-[#FAFAF9]">
                          {editingBooking === b.id ? (
                            /* Edit Form */
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {[["Guest Name","guest_name","text"],["Email","guest_email","email"],["Phone","guest_phone","tel"]].map(([label, field, type]) => (
                                  <div key={field}>
                                    <label className="block text-[10px] tracking-widests uppercase text-[#1C1917] font-bold mb-1.5 font-manrope">{label}</label>
                                    <input type={type} value={bookingEdits[field] || ""} onChange={(e) => setBookingEdits((p) => ({ ...p, [field]: e.target.value }))}
                                      className="w-full bg-white border-b border-[#E7E5E4] py-2 text-sm text-[#1C1917] focus:outline-none focus:border-[#D4AF37] transition-colors font-manrope" />
                                  </div>
                                ))}
                                <div>
                                  <label className="block text-[10px] tracking-widests uppercase text-[#1C1917] font-bold mb-1.5 font-manrope">Number of Guests</label>
                                  <input type="number" min="1" max="20" value={bookingEdits.guest_count || 1}
                                    onChange={(e) => setBookingEdits((p) => ({ ...p, guest_count: parseInt(e.target.value) || 1 }))}
                                    className="w-full bg-white border-b border-[#E7E5E4] py-2 text-sm text-[#1C1917] focus:outline-none focus:border-[#D4AF37] transition-colors font-manrope" />
                                </div>
                                <div>
                                  <label className="block text-[10px] tracking-widests uppercase text-[#1C1917] font-bold mb-1.5 font-manrope">Check-in Time</label>
                                  <select value={bookingEdits.check_in_time || ""} onChange={(e) => setBookingEdits((p) => ({ ...p, check_in_time: e.target.value }))}
                                    className="w-full bg-white border-b border-[#E7E5E4] py-2 text-sm text-[#1C1917] focus:outline-none focus:border-[#D4AF37] transition-colors font-manrope cursor-pointer">
                                    <option value="">Select time</option>
                                    {CHECK_IN_TIMES_ADMIN.map((t) => <option key={t} value={t}>{t}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-[10px] tracking-widests uppercase text-[#1C1917] font-bold mb-1.5 font-manrope">Check-out Time</label>
                                  <select value={bookingEdits.check_out_time || ""} onChange={(e) => setBookingEdits((p) => ({ ...p, check_out_time: e.target.value }))}
                                    className="w-full bg-white border-b border-[#E7E5E4] py-2 text-sm text-[#1C1917] focus:outline-none focus:border-[#D4AF37] transition-colors font-manrope cursor-pointer">
                                    <option value="">Select time</option>
                                    {CHECK_OUT_TIMES_ADMIN.map((t) => <option key={t} value={t}>{t}</option>)}
                                  </select>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <input id={`edit-parking-${b.id}`} type="checkbox" checked={bookingEdits.parking_required || false}
                                  onChange={(e) => setBookingEdits((p) => ({ ...p, parking_required: e.target.checked }))}
                                  className="w-4 h-4 accent-[#D4AF37] cursor-pointer" />
                                <label htmlFor={`edit-parking-${b.id}`} className="text-sm text-[#1C1917] font-manrope cursor-pointer">Parking space required</label>
                              </div>
                              <div>
                                <label className="block text-[10px] tracking-widests uppercase text-[#1C1917] font-bold mb-1.5 font-manrope">Special Requirements</label>
                                <textarea value={bookingEdits.special_requirements || ""} onChange={(e) => setBookingEdits((p) => ({ ...p, special_requirements: e.target.value }))}
                                  rows={2} className="w-full bg-white border-b border-[#E7E5E4] py-2 text-sm text-[#1C1917] focus:outline-none focus:border-[#D4AF37] transition-colors resize-none font-manrope" />
                              </div>
                              <div className="flex gap-3 pt-2">
                                <button onClick={() => handleSaveBooking(b.id)} disabled={bookingSaving}
                                  className="flex items-center gap-2 h-9 px-5 bg-[#1C1917] text-[#D4AF37] text-[10px] tracking-widest uppercase font-semibold font-manrope hover:bg-[#292524] transition-colors rounded-sm disabled:opacity-60">
                                  {bookingSaving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}Save Changes
                                </button>
                                <button onClick={() => setEditingBooking(null)}
                                  className="flex items-center gap-2 h-9 px-4 border border-[#E7E5E4] text-[#A8A29E] text-[10px] tracking-widest uppercase font-manrope hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors rounded-sm">
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* Read View */
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                              <div>
                                <p className="text-[10px] tracking-widest uppercase text-[#A8A29E] font-manrope mb-1">Property</p>
                                <p className="text-[#1C1917] font-manrope">{b.listing_name}</p>
                              </div>
                              <div>
                                <p className="text-[10px] tracking-widests uppercase text-[#A8A29E] font-manrope mb-1">Dates</p>
                                <p className="text-[#1C1917] font-manrope">{b.check_in} → {b.check_out} ({b.nights} night{b.nights !== 1 ? "s" : ""})</p>
                              </div>
                              <div>
                                <p className="text-[10px] tracking-widests uppercase text-[#A8A29E] font-manrope mb-1">Guest Name</p>
                                <p className="text-[#1C1917] font-manrope">{b.guest_name || "—"}</p>
                              </div>
                              <div>
                                <p className="text-[10px] tracking-widests uppercase text-[#A8A29E] font-manrope mb-1">Email</p>
                                <p className="text-[#1C1917] font-manrope break-all">{b.guest_email || "—"}</p>
                              </div>
                              <div>
                                <p className="text-[10px] tracking-widests uppercase text-[#A8A29E] font-manrope mb-1">Phone</p>
                                <p className="text-[#1C1917] font-manrope">{b.guest_phone || "—"}</p>
                              </div>
                              <div>
                                <p className="text-[10px] tracking-widests uppercase text-[#A8A29E] font-manrope mb-1">Guests</p>
                                <p className="text-[#1C1917] font-manrope">{b.guest_count ?? 1}</p>
                              </div>
                              <div>
                                <p className="text-[10px] tracking-widests uppercase text-[#A8A29E] font-manrope mb-1">Check-in Time</p>
                                <p className="text-[#1C1917] font-manrope">{b.check_in_time || "—"}</p>
                              </div>
                              <div>
                                <p className="text-[10px] tracking-widests uppercase text-[#A8A29E] font-manrope mb-1">Check-out Time</p>
                                <p className="text-[#1C1917] font-manrope">{b.check_out_time || "—"}</p>
                              </div>
                              <div>
                                <p className="text-[10px] tracking-widests uppercase text-[#A8A29E] font-manrope mb-1">Parking</p>
                                <p className="text-[#1C1917] font-manrope">{b.parking_required ? "Yes — required" : "Not required"}</p>
                              </div>
                              <div>
                                <p className="text-[10px] tracking-widests uppercase text-[#A8A29E] font-manrope mb-1">Amount Paid</p>
                                <p className="font-playfair text-lg font-semibold text-[#1C1917]">{sym}{b.amount?.toFixed(2)} {cur}</p>
                              </div>
                              {b.special_requirements && (
                                <div className="sm:col-span-2">
                                  <p className="text-[10px] tracking-widests uppercase text-[#A8A29E] font-manrope mb-1">Special Requirements</p>
                                  <p className="text-[#1C1917] font-manrope leading-relaxed">{b.special_requirements}</p>
                                </div>
                              )}
                              {b.selected_dates?.length > 0 && (
                                <div className="sm:col-span-2">
                                  <p className="text-[10px] tracking-widests uppercase text-[#A8A29E] font-manrope mb-2">Booked Nights</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {b.selected_dates.map((d) => (
                                      <span key={d} className="text-[10px] bg-[#FEF3C7] text-[#C5A059] px-2 py-0.5 rounded-sm font-manrope">{d}</span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div className="sm:col-span-2 pt-2 border-t border-[#F0EFEE] flex items-center justify-between">
                                <p className="text-[10px] text-[#C8C4C0] font-manrope">
                                  {b.created_at ? new Date(b.created_at).toLocaleString("en-IE") : ""}
                                </p>
                                <button onClick={() => startEditBooking(b)}
                                  className="flex items-center gap-1.5 h-8 px-4 border border-[#E7E5E4] text-[#A8A29E] text-[10px] tracking-widest uppercase font-manrope hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors rounded-sm">
                                  <Save size={11} />Edit Details
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminDashboard;
